import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import { InteractionStatus } from "@azure/msal-browser";
import { Loader2, ShieldX } from "lucide-react";
import { useTranslation } from "react-i18next";
import Header from "@/components/layout/Header";
import Sidebar, { type SidebarView } from "@/components/layout/Sidebar";
import ChatRuntime from "@/components/chat/ChatRuntime";
import ChatThread from "@/components/chat/ChatThread";
import ApprovalDetail from "@/components/approvals/ApprovalDetail";
import { useApprovalsInbox } from "@/lib/approvals/useApprovalsInbox";
import { createApiClient } from "@/lib/api/chatApi";
import type { Assistant } from "@/lib/api/chatApi";
import { useAuth } from "@/lib/auth/useAuth";
import type { LinkContext } from "@/lib/context";

export default function ChatPage() {
  const { getToken, logout, inProgress } = useAuth();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const api = useMemo(() => createApiClient(getToken, logout), [getToken, logout]);

  const [agents, setAgents] = useState<Assistant[]>([]);
  const [agentsLoaded, setAgentsLoaded] = useState(false);
  // null = no explicit agent chosen → a new chat runs in auto-router mode.
  // A concrete value is set when reopening a thread or when the user picks an agent.
  const [selectedAgent, setSelectedAgent] = useState<string | null>(
    () => searchParams.get("agent"),
  );
  const [activeThread, setActiveThread] = useState<{ threadId: string; assistantId: string } | null>(
    () => {
      const thread = searchParams.get("thread");
      const agent = searchParams.get("agent");
      return (thread && agent) ? { threadId: thread, assistantId: agent } : null;
    },
  );
  const [chatCounter, setChatCounter] = useState(0);
  // Hilo de la conversacion en pantalla, tambien el que nace en el primer turno
  // de un chat nuevo (activeThread solo cubre los reabiertos): lo usan la
  // seleccion de la sidebar y el titulo de la pestaña.
  const [hiloEnCurso, setHiloEnCurso] = useState<string | null>(null);

  // Vista del panel: chats o bandeja de aprobaciones (sidebar conmuta). Arranca
  // en aprobaciones si la URL trae ?view=approvals (el menu / el redirect de
  // /approvals lo usan). El sidebar solo ofrece el conmutador si hay pendientes.
  const [view, setView] = useState<SidebarView>(
    () => (searchParams.get("view") === "approvals" ? "approvals" : "chats"),
  );

  // Click en una notificacion del navegador: abre la bandeja y selecciona esa
  // aprobacion. `inbox.setSelectedId` no existe aun aqui, asi que apuntamos a el
  // via ref (rellenado justo despues de crear el hook).
  const selectApprovalRef = useRef<(id: string) => void>(() => {});
  const handleNotificationClick = useCallback((item: { correlation_id: string }) => {
    setView("approvals");
    selectApprovalRef.current(item.correlation_id);
  }, []);

  const inbox = useApprovalsInbox({ onNotificationClick: handleNotificationClick });
  selectApprovalRef.current = inbox.setSelectedId;

  // Al entrar en la pestana Aprobaciones (gesto del usuario), pedimos permiso de
  // notificaciones si aun no se ha decidido. Los navegadores exigen gesto para
  // este prompt, y esta pestana es justo el contexto donde el aviso tiene sentido.
  const changeView = useCallback(
    (next: SidebarView) => {
      setView(next);
      if (next === "approvals" && inbox.notifPermission === "default") {
        void inbox.requestNotificationPermission();
      }
    },
    [inbox.notifPermission, inbox.requestNotificationPermission],
  );

  // La pestana Aprobaciones permanece visible si el usuario tiene permiso a un agente
  // HITL (inbox.canApprove), aunque la bandeja quede a 0. Solo salimos de la vista de
  // aprobaciones si el usuario NO puede aprobar (p.ej. pierde el permiso): ahi no hay
  // pestana donde quedarse. Ya NO redirigimos solo por quedar 0 pendientes.
  useEffect(() => {
    if (view === "approvals" && !inbox.loading && !inbox.canApprove) {
      setView("chats");
    }
  }, [view, inbox.loading, inbox.canApprove]);

  // Contexto de un enlace directo, de LinkPage via router state. Se da por
  // consumido cuando el backend acepta el turno que lo lleva, y vuelve si ese
  // turno falla o lo bloquea el guardrail (ChatRuntime, `turnoDeEnlace`).
  const [linkContext, setLinkContext] = useState<LinkContext | null>(() => {
    const state = location.state as { linkContext?: LinkContext } | null;
    if (state?.linkContext) {
      window.history.replaceState({}, "");
      return state.linkContext;
    }
    return null;
  });

  // Only fetch data when MSAL is not processing a redirect/interaction
  useEffect(() => {
    if (inProgress !== InteractionStatus.None) return;
    api.listAssistants().then((a) => {
      setAgents(a);
      setAgentsLoaded(true);
      // Drop a stale/invalid explicit agent; otherwise keep it. null = auto mode.
      setSelectedAgent((prev) => (prev && a.some((ag) => ag.graph_id === prev) ? prev : null));
    }).catch(console.error);
  }, [api, inProgress]);

  // Open thread from URL query params (?thread=X&agent=Y) or pre-select agent (?agent=Y)
  useEffect(() => {
    const thread = searchParams.get("thread");
    const agent = searchParams.get("agent");
    if (thread && agent) {
      setActiveThread({ threadId: thread, assistantId: agent });
      setSelectedAgent(agent);
      setSearchParams({}, { replace: true });
    } else if (agent) {
      setSelectedAgent(agent);
      setActiveThread(null);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleNewChat = () => {
    setActiveThread(null);
    setSelectedAgent(null);  // siempre arrancar en modo auto al pulsar "Nueva conversacion"
    setChatCounter((c) => c + 1);
  };

  const handleSelectThread = (threadId: string, assistantId: string) => {
    setActiveThread({ threadId, assistantId });
    setSelectedAgent(assistantId);
  };

  const handleChangeAgent = (agentId: string) => {
    setSelectedAgent(agentId);
    setActiveThread(null);
  };

  // Effective agent for the runtime: a reopened thread uses its own agent; an
  // explicit pick uses that agent; otherwise a new chat runs in auto-router mode.
  const effectiveAssistantId = activeThread?.assistantId ?? selectedAgent ?? "auto";
  const chatKey = activeThread ? activeThread.threadId : `new-${selectedAgent ?? "auto"}-${chatCounter}`;

  return (
    <div className="flex h-screen flex-col">
      <Header />
      <div className="flex flex-1 overflow-hidden">
        <Sidebar
          api={api}
          onNewChat={() => { setView("chats"); handleNewChat(); }}
          activeThreadId={view === "chats" ? (activeThread?.threadId ?? hiloEnCurso) : null}
          onSelectThread={(tid, aid) => { setView("chats"); handleSelectThread(tid, aid); }}
          view={view}
          onChangeView={changeView}
          approvalItems={inbox.items}
          hitlAgents={inbox.hitlAgents}
          approvalsLoading={inbox.loading}
          canApprove={inbox.canApprove}
          selectedApprovalId={inbox.selectedId}
          onSelectApproval={(item) => { setView("approvals"); inbox.setSelectedId(item.correlation_id); }}
          resolvedByAgent={inbox.resolvedByAgent}
          resolvedLoading={inbox.resolvedLoading}
          resolvedHasMore={inbox.resolvedHasMore}
          onExpandResolved={inbox.loadResolved}
          onLoadMoreResolved={inbox.loadMoreResolved}
          pendingHasMore={inbox.pendingHasMore}
          activityByAgent={inbox.activityByAgent}
          activityLoading={inbox.activityLoading}
          activityHasMore={inbox.activityHasMore}
          onExpandActivity={inbox.loadActivity}
          onLoadMoreActivity={inbox.loadMoreActivity}
        />
        <main className="flex flex-1 flex-col overflow-hidden">
          {view === "approvals" ? (
            <ApprovalDetail
              item={inbox.selected}
              onResume={inbox.resume}
              onReprocess={inbox.reprocess}
              onResolved={inbox.onResolved}
            />
          ) : agentsLoaded && agents.length > 0 ? (
            <ChatRuntime
              key={chatKey}
              api={api}
              assistantId={effectiveAssistantId}
              threadId={activeThread?.threadId}
              agents={agents}
              onChangeAgent={handleChangeAgent}
              linkContext={linkContext}
              onLinkContextConsumed={() => setLinkContext(null)}
              onLinkContextRestored={setLinkContext}
              onThreadIdChange={setHiloEnCurso}
            >
              <ChatThread />
            </ChatRuntime>
          ) : agentsLoaded && agents.length === 0 ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 px-4 text-center">
              <ShieldX size={36} className="text-[var(--muted-foreground)]" />
              <p className="text-sm font-medium text-[var(--foreground)]">{t("chat.noAgents")}</p>
              <p className="text-xs text-[var(--muted-foreground)]">{t("chat.noAgentsDesc")}</p>
            </div>
          ) : (
            <div className="flex flex-1 items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-[var(--muted-foreground)]" />
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
